import React, { useLayoutEffect, useRef, useState } from 'react'
import { FlagIcon, CheckIcon, XMarkIcon, UserIcon, WrenchScrewdriverIcon, TrophyIcon, BoltIcon } from '@heroicons/react/24/solid'

/**
 * Wspólny język wizualny raportu „Sprawdź ofertę” (przebudowa 2026-09-30, uwaga właściciela:
 * pastelowe ramki z ikoną i akapitem wyglądały jak „z generatora”, a tekstu było za dużo).
 *
 * - `Znak`: jeden zestaw znaczników statusu dla flag, zgodności, doboru modułów i PME2.
 *   Kolor niesie znacznik, a nie tło całej ramki.
 * - `MiniSkala`: ten sam motyw co pasek ceny na górze raportu (szary tor, niebieski
 *   „pasujący” zakres, kropka Twojej oferty), w małej skali, np. przy mocy falownika.
 * - `BateriaPme2`: pojemność magazynu wobec wymaganej przez PME2, narysowana jak bateria.
 */

const ZNAKI = {
  czerwona: { tlo: 'from-red-500 to-red-600 shadow-red-500/30', Ikona: FlagIcon, opis: 'Czerwona flaga' },
  nie: { tlo: 'from-red-500 to-red-600 shadow-red-500/30', Ikona: XMarkIcon, opis: 'Niespełnione' },
  pomaranczowa: { tlo: 'from-amber-400 to-amber-500 shadow-amber-500/30', znak: '?', opis: 'Do wyjaśnienia' },
  sprawdz: { tlo: 'from-amber-400 to-amber-500 shadow-amber-500/30', znak: '?', opis: 'Do sprawdzenia' },
  info: { tlo: 'from-sky-500 to-sky-600 shadow-sky-500/30', Ikona: WrenchScrewdriverIcon, opis: 'Przy montażu' },
  tak: { tlo: 'from-emerald-500 to-emerald-600 shadow-emerald-500/30', Ikona: CheckIcon, opis: 'W porządku' },
  ty: { tlo: 'from-slate-400 to-slate-500 shadow-slate-500/30', Ikona: UserIcon, opis: 'Zależy od Ciebie' },
  brak: { tlo: 'from-slate-300 to-slate-400 shadow-slate-400/30', znak: '?', opis: 'Brak danych' },
  wygrana: { tlo: 'from-emerald-500 to-emerald-600 shadow-emerald-500/30', Ikona: TrophyIcon, opis: 'Wybór' },
}

// sm: w wierszach listy, tytul: obok nagłówka werdyktu, md: przy flagach i statusach sekcji.
const ROZMIARY = {
  sm: { pole: 'h-5 w-5 rounded-full', ikona: 'h-3 w-3', tekst: 'text-[11px]' },
  tytul: { pole: 'h-6 w-6 rounded-full', ikona: 'h-4 w-4', tekst: 'text-sm' },
  md: { pole: 'h-9 w-9 rounded-xl', ikona: 'h-5 w-5', tekst: 'text-base' },
}

export const opisZnaku = (rodzaj) => ZNAKI[rodzaj]?.opis || ''

export const Znak = ({ rodzaj, rozmiar = 'md', className = '' }) => {
  const z = ZNAKI[rodzaj] || ZNAKI.brak
  const r = ROZMIARY[rozmiar]
  return (
    <span role="img" aria-label={z.opis}
          className={`inline-flex flex-shrink-0 items-center justify-center bg-gradient-to-b text-white shadow-md ${z.tlo} ${r.pole} ${className}`}>
      {z.Ikona ? <z.Ikona className={r.ikona} aria-hidden="true" /> : <span className={`font-bold leading-none ${r.tekst}`} aria-hidden="true">{z.znak}</span>}
    </span>
  )
}

/** Etykieta nad lub pod paskiem: przy brzegach kotwiczona krawędzią, żeby nie wyszła poza kartę. */
const kotwica = (x) => (x < 12 ? '0%' : x > 88 ? '-100%' : '-50%')

const KROPKA = {
  czerwona: { obwodka: 'border-red-600', tekst: 'text-red-700' },
  pomaranczowa: { obwodka: 'border-amber-500', tekst: 'text-amber-700' },
  info: { obwodka: 'border-sky-600', tekst: 'text-sky-700' },
}

/**
 * Mała skala: pasujący zakres `od`–`do` i wartość z oferty. Etykieta wartości nad kropką,
 * etykieta zakresu pod nim. Dla czytników ekranu wystarcza tekst obok (flaga ma go w tytule).
 */
export const MiniSkala = ({ od, do: doK, wartosc, etykietaZakresu, etykietaWartosci, poziom = 'pomaranczowa' }) => {
  const lo = Math.max(0, Math.min(od, wartosc) * 0.7)
  const hi = Math.max(doK, wartosc) * 1.15
  const proc = (v) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100))
  const [a, b, x] = [proc(od), proc(doK), proc(wartosc)]
  const k = KROPKA[poziom] || KROPKA.pomaranczowa
  return (
    <div className="relative mt-2 h-[52px] max-w-md" aria-hidden="true">
      <span className={`absolute top-0 whitespace-nowrap text-[11px] font-semibold tabular-nums ${k.tekst}`}
            style={{ left: `${x}%`, transform: `translateX(${kotwica(x)})` }}>{etykietaWartosci}</span>
      <div className="absolute inset-x-0 top-[22px] h-2 rounded-full bg-gray-100 shadow-inner" />
      <div className="absolute top-[22px] h-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"
           style={{ left: `${a}%`, width: `${b - a}%` }} />
      <div className={`absolute top-[18px] h-4 w-4 -translate-x-1/2 rounded-full border-[4px] bg-white shadow ${k.obwodka}`}
           style={{ left: `${x}%` }} />
      <span className="absolute top-[36px] whitespace-nowrap text-[11px] font-semibold tabular-nums text-blue-700"
            style={{ left: `${(a + b) / 2}%`, transform: `translateX(${kotwica((a + b) / 2)})` }}>{etykietaZakresu}</span>
    </div>
  )
}

const kwh = (v) => `${String(Math.round(v * 100) / 100).replace('.', ',')} kWh`

/**
 * Bateria: wypełnienie to pojemność z oferty, przerywana linia to minimum programu,
 * zielone pole za nią to „strefa dotacji”.
 *
 * Kolor jak wskaźnik naładowania (prośba właściciela 2026-09-30): gradient rozpięty na
 * CAŁEJ długości baterii, od czerwieni przez pomarańcz do żółci tuż przed progiem i zieleni
 * za nim. Wypełnienie odsłania go tylko do swojego poziomu, więc za mały magazyn kończy się
 * na czerwieni, pomarańczu albo żółci, a wystarczający dochodzi do zieleni.
 *
 * Napis „w ofercie …” stoi w pasku, gdy się mieści (mierzone na żywo), a inaczej pod nim.
 */
/**
 * Plakietka „w ofercie 13,3 kWh” na pasku baterii (prośba właściciela 2026-09-30: zamiast
 * zwykłego czarnego napisu). Jasna, lekko przezroczysta, z cieniem, więc czyta się na
 * każdym kolorze gradientu. Kolory wpisane na stałe: klasy `bg-white` i `text-gray-*`
 * zmieniają się w ciemnym motywie, a pasek zostaje kolorowy w obu.
 */
const PlakietkaBaterii = ({ kwhTekst, ukryta = false, style, className = '' }, ref) => (
  <span ref={ref} aria-hidden={ukryta || undefined} style={{ backgroundColor: 'rgba(255,255,255,0.92)', ...style }}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full py-1 pl-1.5 pr-2.5 shadow-md ring-1 ring-black/5 ${ukryta ? 'invisible' : ''} ${className}`}>
    <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: '#fef3c7' }}>
      <BoltIcon className="h-3.5 w-3.5" style={{ color: '#d97706' }} aria-hidden="true" />
    </span>
    <span className="text-[10px] font-semibold" style={{ color: '#64748b' }}>w ofercie</span>
    <span className="text-sm font-extrabold tabular-nums tracking-tight" style={{ color: '#0f172a' }}>{kwhTekst}</span>
  </span>
)
const Plakietka = React.forwardRef(PlakietkaBaterii)

const kolorBaterii = (prog) => `linear-gradient(90deg, #ef4444 0%, #f97316 ${prog * 0.55}%, `
  + `#facc15 ${prog * 0.95}%, #22c55e ${Math.min(100, prog + 4)}%, #16a34a 100%)`

export const BateriaPme2 = ({ pojemnosc, wymagane }) => {
  const hi = Math.max(pojemnosc, wymagane) * 1.3
  const p = (v) => Math.min(100, (v / hi) * 100)
  const [pk, pw] = [p(pojemnosc), p(wymagane)]
  const ok = pojemnosc >= wymagane - 1e-9
  // Linia progu leży na wnętrzu baterii (bez ramki i odstępu 4 px z każdej strony).
  const naWnetrzu = (x) => `calc(4px + (100% - 8px) * ${x / 100})`
  const podpis = `w ofercie ${kwh(pojemnosc)}`

  // Gdzie postawić podpis w wypełnieniu: odstęp od jego prawego końca w px albo null, gdy
  // się nie mieści (wtedy pod paskiem, jak dawniej; tak też w testach i bez pomiaru).
  // Przerywana linia progu nie może przeciąć napisu: gdy wypada w jego miejscu, napis
  // staje przed linią.
  const wypelnienie = useRef(null)
  const miara = useRef(null)
  const [odPrawej, setOdPrawej] = useState(null)
  useLayoutEffect(() => {
    const el = wypelnienie.current
    if (!el || !miara.current || pk <= 0) return undefined
    const sprawdz = () => {
      const fw = el.offsetWidth
      const lw = miara.current.offsetWidth
      const prog = ((fw * 100) / pk) * (pw / 100)
      const MARGINES = 12
      const koniec = fw - MARGINES
      if (lw + 2 * MARGINES > fw) { setOdPrawej(null); return }
      // Linia progu w miejscu napisu przy końcu wypełnienia?
      if (prog > koniec - lw - 8 && prog < fw + 4) {
        const przedLinia = prog - 10
        setOdPrawej(przedLinia - lw >= MARGINES ? fw - przedLinia : null)
        return
      }
      setOdPrawej(MARGINES)
    }
    sprawdz()
    if (typeof ResizeObserver === 'undefined') return undefined
    const obs = new ResizeObserver(sprawdz)
    obs.observe(el)
    return () => obs.disconnect()
  }, [pk, pw])
  const wSrodku = odPrawej != null

  return (
    <div className="mt-5" role="img" aria-label={`Magazyn w ofercie ${kwh(pojemnosc)}, program wymaga co najmniej ${kwh(wymagane)}.`}>
      <div className="relative h-5 text-[11px] font-semibold text-gray-700">
        <span className="absolute whitespace-nowrap" style={{ left: naWnetrzu(pw), transform: `translateX(${kotwica(pw)})` }}>
          wymagane {kwh(wymagane)}
        </span>
      </div>
      <div className="flex items-center">
        <div className="relative h-12 flex-1 rounded-xl border-2 border-gray-300 bg-white">
          <div className="absolute inset-1 overflow-hidden rounded-lg bg-gray-100">
            <div className="absolute inset-y-0 right-0 bg-emerald-100" style={{ left: `${pw}%` }} />
            {/* Gradient liczony dla pełnej długości: tło szerokie jak cały tor, przycięte do wypełnienia. */}
            <div ref={wypelnienie} className="absolute inset-y-0 left-0 rounded-lg shadow-sm"
                 style={{ width: `${pk}%`, backgroundImage: kolorBaterii(pw), backgroundSize: `${pk > 0 ? 10000 / pk : 100}% 100%` }}>
              {wSrodku && (
                <Plakietka kwhTekst={kwh(pojemnosc)} className="absolute top-1/2 -translate-y-1/2" style={{ right: odPrawej }} />
              )}
            </div>
          </div>
          <div className="absolute -bottom-1.5 -top-1.5 border-l-2 border-dashed border-gray-700" style={{ left: naWnetrzu(pw) }} />
        </div>
        <div className="h-5 w-1.5 rounded-r-md bg-gray-300" />
      </div>
      <div className="relative mt-1.5 h-5 text-[11px] font-semibold">
        {!wSrodku && (
          <span className={`absolute whitespace-nowrap ${ok ? 'text-emerald-700' : 'text-red-700'}`}
                style={{ left: naWnetrzu(pk), transform: `translateX(${kotwica(pk)})` }}>
            {podpis}
          </span>
        )}
        {/* Niewidoczna miara plakietki: ta sama, co na pasku, żeby wiedzieć, czy się zmieści. */}
        <Plakietka ref={miara} kwhTekst={kwh(pojemnosc)} ukryta className="absolute left-0 top-0" />
      </div>
    </div>
  )
}
